// ANANT YOJANA MITRA — AI-powered government scheme discovery
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Sparkles, Loader2, Search, ExternalLink, Youtube, Download, Copy, Users, Wallet, ShieldCheck, RefreshCw, Filter, LayoutGrid, Globe, PlayCircle, Home, MessageCircle, FileText, RotateCw, CheckCircle2, XCircle, Clock } from 'lucide-react';
import ATWalletBadge from '../../components/ATWalletBadge';
import api from '../../utils/api';

const CASTE_OPTS = ['General', 'OBC', 'SC', 'ST', 'EWS', 'Any'];
const GENDER_OPTS = ['Male', 'Female', 'Other'];
const INCOME_OPTS = ['Below 1L', '1L-3L', '3L-5L', '5L-8L', '8L+'];
const PROF_OPTS = ['Farmer', 'Student', 'Salaried', 'Self-Employed', 'Homemaker', 'Retired', 'Unemployed', 'Other'];

export default function AnantYojana() {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState({ age: '', gender: '', caste: '', income_range: '', state: '', profession: '' });
  const [schemes, setSchemes] = useState([]);
  const [profileSet, setProfileSet] = useState(false);
  const [savingProfile, setSavingProfile] = useState(false);
  const [subscribing, setSubscribing] = useState(false);
  const [catFilter, setCatFilter] = useState('All');
  const [detailScheme, setDetailScheme] = useState(null);
  const [nowTick, setNowTick] = useState(Date.now());
  // Tab: schemes | csc
  const [mainTab, setMainTab] = useState('schemes');
  const [cscGrouped, setCscGrouped] = useState({});
  const [cscCategories, setCscCategories] = useState([]);
  const [detailCSC, setDetailCSC] = useState(null);

  useEffect(() => { const t = setInterval(() => setNowTick(Date.now()), 1000); return () => clearInterval(t); }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/anant-yojana/config');
      setConfig(r.data);
      if (r.data?.profile) { setProfile(r.data.profile); setProfileSet(true); }
    } catch (e) { toast.error('Load failed'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const loadSchemes = useCallback(async () => {
    if (!config?.subscription?.active) return;
    try {
      const r = await api.get('/anant-yojana/schemes', { params: catFilter !== 'All' ? { category: catFilter } : {} });
      setSchemes(r.data?.schemes || []);
    } catch (e) { /* silent for paywall */ }
  }, [config, catFilter]);
  useEffect(() => { if (config?.subscription?.active && profileSet) loadSchemes(); }, [config, profileSet, loadSchemes]);

  // Load CSC digital services (Netflix-style catalog)
  const loadCSC = useCallback(async () => {
    if (!config?.subscription?.active) return;
    try {
      const r = await api.get('/anant-yojana/csc/services');
      setCscGrouped(r.data?.grouped || {});
      setCscCategories(r.data?.categories || []);
    } catch (e) { /* silent */ }
  }, [config]);
  useEffect(() => { if (config?.subscription?.active && mainTab === 'csc') loadCSC(); }, [config, mainTab, loadCSC]);

  const countdown = useMemo(() => {
    const exp = config?.subscription?.expires_at;
    if (!exp) return null;
    const ms = new Date(exp).getTime() - nowTick;
    if (ms <= 0) return { expired: true, dd: 0, hh: 0, mm: 0, ss: 0 };
    const s = Math.floor(ms / 1000);
    return { expired: false, dd: Math.floor(s / 86400), hh: Math.floor((s % 86400) / 3600), mm: Math.floor((s % 3600) / 60), ss: s % 60 };
  }, [config, nowTick]);
  const fmt2 = (n) => String(n).padStart(2, '0');

  const activeSub = config?.subscription?.active && !(countdown && countdown.expired);
  const isTrial = config?.subscription?.is_trial;

  const saveProfile = async () => {
    if (!profile.age || !profile.gender || !profile.state || !profile.profession || !profile.income_range || !profile.caste) {
      toast.error('Fill all profile fields'); return;
    }
    setSavingProfile(true);
    try {
      await api.post('/anant-yojana/profile', { ...profile, age: parseInt(profile.age) });
      setProfileSet(true);
      toast.success('Profile saved — schemes filtering');
      loadSchemes();
    } catch (e) { toast.error('Save failed'); }
    finally { setSavingProfile(false); }
  };

  const subscribe = async (planId) => {
    if (!window.confirm(`Subscribe to Anant Yojana Mitra — ${planId} plan?`)) return;
    setSubscribing(true);
    try {
      await api.post('/anant-yojana/subscribe', { plan_id: planId });
      toast.success('🎉 Subscribed! Full access unlocked + earnings enabled');
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Subscribe failed'); }
    finally { setSubscribing(false); }
  };

  const downloadPDF = async () => {
    // Client-side PDF via jsPDF (dynamic import; falls back to text file if not installed)
    try {
      const { jsPDF } = await import('jspdf');
      const doc = new jsPDF();
      let y = 15;
      doc.setFontSize(16); doc.setTextColor(30, 100, 50);
      doc.text('Anant Yojana Mitra — My Scheme Recommendations', 15, y); y += 8;
      doc.setFontSize(10); doc.setTextColor(80);
      doc.text(`Prepared for: ${config?.subscription?.plan_label || 'User'}   ·   Powered by Anant Web Portal`, 15, y); y += 10;
      schemes.forEach((s, i) => {
        if (y > 265) { doc.addPage(); y = 15; }
        doc.setFontSize(12); doc.setTextColor(20, 20, 100);
        doc.text(`${i + 1}. ${s.title}`, 15, y); y += 6;
        doc.setFontSize(9); doc.setTextColor(80);
        const desc = doc.splitTextToSize(s.description || '', 180);
        doc.text(desc.slice(0, 4), 15, y); y += desc.slice(0, 4).length * 4;
        doc.setTextColor(0, 120, 60);
        doc.text(`Category: ${s.category}   Apply: ${s.apply_link || 'N/A'}`, 15, y); y += 8;
        doc.setDrawColor(200); doc.line(15, y, 195, y); y += 4;
      });
      doc.save('anant-yojana-schemes.pdf');
      toast.success('PDF downloaded');
    } catch (e) {
      // Fallback plain-text export
      const text = 'ANANT YOJANA MITRA — Scheme Recommendations\n\n' + schemes.map((s, i) => `${i + 1}. ${s.title}\n${s.description}\nCategory: ${s.category}\nApply: ${s.apply_link || 'N/A'}\n${'-'.repeat(50)}\n`).join('\n');
      const blob = new Blob([text], { type: 'text/plain' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url; a.download = 'anant-yojana-schemes.txt'; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
      toast.success('Text file downloaded');
    }
  };

  const copyReferral = () => {
    const link = `${window.location.origin}/anant-yojana?ref=${config?.subscription?.username || 'you'}`;
    navigator.clipboard.writeText(link).then(() => toast.success('Referral link copied!'));
  };

  const shareWhatsApp = () => {
    const link = `${window.location.origin}/anant-yojana`;
    const text = `🎯 *Anant Yojana Mitra* — AI se government schemes discover karo apne profile ke hisaab se!\n\n✅ 48h free trial\n✅ Auto scheme match by age/state/income\n✅ Earn 7-level Affiliate Network commission\n\nJoin: ${link}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank');
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-slate-950 text-white"><Loader2 className="w-8 h-8 animate-spin" /></div>;

  return (
    <div className="min-h-screen pt-20 bg-gradient-to-b from-emerald-950 via-slate-900 to-black text-white">
      {/* AT wallet bar — same solid style as other tools */}
      <div className="sticky top-16 z-40 bg-slate-950 backdrop-blur-xl border-b-2 border-emerald-400/50 px-4 py-2 flex items-center justify-between shadow-2xl shadow-black">
        <Link to="/" className="text-xs text-slate-400 hover:text-white flex items-center gap-1"><span>← Home</span></Link>
        <ATWalletBadge compact />
      </div>

      <div className="max-w-6xl mx-auto px-4 py-6">
        {/* Hero */}
        <div className="relative overflow-hidden rounded-3xl border-2 border-emerald-400/40 bg-gradient-to-br from-emerald-950 via-teal-900/50 to-cyan-950 p-6 mb-6" data-testid="yojana-hero">
          <div className="absolute -top-20 -right-20 w-64 h-64 rounded-full bg-emerald-500/20 blur-3xl pointer-events-none animate-pulse"></div>
          <div className="absolute -bottom-20 -left-20 w-64 h-64 rounded-full bg-teal-500/20 blur-3xl pointer-events-none animate-pulse"></div>
          <div className="relative text-center">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-gradient-to-r from-emerald-500/25 to-teal-500/25 border border-emerald-400/40 text-[10px] font-black tracking-widest text-emerald-200 mb-3">
              <Sparkles className="w-3 h-3 animate-pulse" /> ANANT YOJANA MITRA · 48H FREE TRIAL
            </div>
            <h1 className="text-4xl sm:text-5xl font-black bg-gradient-to-r from-emerald-300 via-teal-200 to-cyan-300 bg-clip-text text-transparent drop-shadow-2xl">अनंत योजना मित्र</h1>
            <p className="text-sm text-slate-200 mt-2 max-w-2xl mx-auto">AI-powered <b className="text-emerald-300">Government Scheme Discovery</b> + <b className="text-cyan-300">CSC Digital Services Hub</b> — apne profile ke hisaab se schemes, direct apply link, video tutorial, PDF download aur 7-level Affiliate Network earnings.</p>
            <div className="flex justify-center gap-2 mt-4 flex-wrap">
              <div className="px-3 py-1.5 rounded-lg bg-emerald-500/15 border border-emerald-400/30 text-[10px] font-black text-emerald-200">📋 8+ Schemes</div>
              <div className="px-3 py-1.5 rounded-lg bg-cyan-500/15 border border-cyan-400/30 text-[10px] font-black text-cyan-200">🌐 10+ CSC Services</div>
              <div className="px-3 py-1.5 rounded-lg bg-amber-500/15 border border-amber-400/30 text-[10px] font-black text-amber-200">💰 7-Level Affiliate Network</div>
              <div className="px-3 py-1.5 rounded-lg bg-fuchsia-500/15 border border-fuchsia-400/30 text-[10px] font-black text-fuchsia-200">🎯 AI Profile Match</div>
            </div>
          </div>
        </div>

        {/* Subscription/Trial Header */}
        {activeSub && (
          <div className={`rounded-2xl border-2 ${isTrial ? 'border-amber-400/40 bg-amber-500/10' : 'border-emerald-400/40 bg-emerald-500/10'} p-3 mb-4 flex items-center justify-between flex-wrap gap-2`} data-testid="yojana-sub-badge">
            <div>
              <div className={`text-[10px] uppercase tracking-widest font-black ${isTrial ? 'text-amber-300' : 'text-emerald-300'}`}>
                {isTrial ? '🆓 FREE TRIAL · 48 HOURS' : '✅ ACTIVE SUBSCRIPTION'}
              </div>
              <div className="text-sm mt-0.5">
                <b>{config.subscription.plan_label}</b>
                {countdown && !countdown.expired && (
                  <> · <span className="font-mono text-amber-200" data-testid="yojana-countdown">{fmt2(countdown.dd)}d : {fmt2(countdown.hh)}h : {fmt2(countdown.mm)}m : {fmt2(countdown.ss)}s left</span></>
                )}
              </div>
            </div>
            <a href="#yojana-plans" className="text-xs px-3 py-1 rounded bg-amber-500 text-black font-black" data-testid="yojana-upgrade-jump">
              {isTrial ? 'Upgrade Now →' : 'Extend / Renew →'}
            </a>
          </div>
        )}

        {/* PLANS — always visible so users on trial or active sub can upgrade/renew */}
        {activeSub && (
          <div id="yojana-plans" className="rounded-3xl border-2 border-emerald-400/30 bg-gradient-to-br from-emerald-950/30 via-slate-900 to-teal-950/30 p-5 mb-6" data-testid="yojana-plans-panel">
            <div className="text-center mb-3">
              <div className="text-lg font-black text-emerald-200">💎 Subscription Plans</div>
              <div className="text-[11px] opacity-70 mt-0.5">Upgrade to unlock earnings + zero interruption · Any plan stacks on top of active</div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {(config?.plans || []).map((p) => (
                <div key={p.id} className={`rounded-2xl border-2 ${p.id === 'yearly' ? 'border-amber-400 bg-amber-500/10' : 'border-emerald-400/40 bg-emerald-500/5'} p-4 text-center`} data-testid={`yojana-plan-inline-${p.id}`}>
                  <div className="text-[10px] uppercase tracking-widest opacity-70">{p.label}</div>
                  <div className="text-3xl font-black mt-1">{p.amount} <span className="text-sm opacity-70">AT</span></div>
                  <div className="text-[10px] opacity-70 mt-0.5">{p.days} days · {p.highlight}</div>
                  <button onClick={() => subscribe(p.id)} disabled={subscribing} data-testid={`yojana-subscribe-inline-${p.id}`} className={`w-full mt-3 px-3 py-2 rounded-lg font-black text-sm ${p.id === 'yearly' ? 'bg-gradient-to-r from-amber-400 to-yellow-300 text-black' : 'bg-gradient-to-r from-emerald-500 to-teal-500 text-white'} disabled:opacity-40`}>
                    {subscribing ? 'Processing…' : `Subscribe · ${p.amount} AT`}
                  </button>
                </div>
              ))}
            </div>
            <div className="mt-3 text-[10px] text-center opacity-70">Affiliate Network Rates: 10% · 7% · 5% · 4% · 3% · 2% · 2% across 7 levels (33% distributed)</div>
          </div>
        )}

        {/* PAYWALL */}
        {!activeSub && (
          <div className="rounded-3xl border-2 border-emerald-400/40 bg-gradient-to-br from-emerald-950/40 via-slate-900 to-teal-950/40 p-6 mb-6" data-testid="yojana-paywall">
            <div className="text-center mb-4">
              <div className="text-2xl font-black text-emerald-200">🔒 Trial Expired / Not Started</div>
              <div className="text-sm opacity-80 mt-1">Subscribe to unlock full scheme discovery + 7-level Affiliate Network earnings</div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {(config?.plans || []).map((p) => (
                <div key={p.id} className={`rounded-2xl border-2 ${p.id === 'yearly' ? 'border-amber-400 bg-amber-500/10' : 'border-emerald-400/40 bg-emerald-500/5'} p-4 text-center`} data-testid={`yojana-plan-${p.id}`}>
                  <div className="text-[10px] uppercase tracking-widest opacity-70">{p.label}</div>
                  <div className="text-3xl font-black mt-1">{p.amount} <span className="text-sm opacity-70">AT</span></div>
                  <div className="text-[10px] opacity-70 mt-0.5">{p.days} days · {p.highlight}</div>
                  <button onClick={() => subscribe(p.id)} disabled={subscribing} data-testid={`yojana-subscribe-${p.id}`} className={`w-full mt-3 px-3 py-2 rounded-lg font-black text-sm ${p.id === 'yearly' ? 'bg-gradient-to-r from-amber-400 to-yellow-300 text-black' : 'bg-gradient-to-r from-emerald-500 to-teal-500 text-white'} disabled:opacity-40`}>
                    {subscribing ? 'Processing…' : `Subscribe · ${p.amount} AT`}
                  </button>
                </div>
              ))}
            </div>
            <div className="mt-3 text-[10px] text-center opacity-70">Affiliate Network Rates: 10% · 7% · 5% · 4% · 3% · 2% · 2% across 7 levels (33% distributed)</div>
          </div>
        )}

        {/* PROFILE + SCHEMES (only if active) */}
        {activeSub && (
          <>
            {/* ===== MAIN TAB SWITCHER (Schemes / Digital Services) ===== */}
            <div className="flex gap-1 p-1 rounded-2xl border border-slate-700 bg-slate-900/70 mb-4" data-testid="yojana-main-tabs">
              <button
                onClick={() => setMainTab('schemes')}
                data-testid="yj-tab-schemes"
                className={`flex-1 px-3 py-2 rounded-xl text-xs font-black transition-all flex items-center justify-center gap-1.5 ${mainTab === 'schemes' ? 'bg-gradient-to-r from-emerald-500 to-teal-500 text-black shadow-lg' : 'text-slate-400 hover:text-white'}`}>
                <LayoutGrid className="w-3.5 h-3.5" /> 📋 Govt Schemes
              </button>
              <button
                onClick={() => setMainTab('csc')}
                data-testid="yj-tab-csc"
                className={`flex-1 px-3 py-2 rounded-xl text-xs font-black transition-all flex items-center justify-center gap-1.5 ${mainTab === 'csc' ? 'bg-gradient-to-r from-cyan-500 to-blue-500 text-black shadow-lg' : 'text-slate-400 hover:text-white'}`}>
                <Globe className="w-3.5 h-3.5" /> 🌐 CSC
              </button>
              <button
                onClick={() => setMainTab('land')}
                data-testid="yj-tab-land"
                className={`flex-1 px-3 py-2 rounded-xl text-xs font-black transition-all flex items-center justify-center gap-1.5 ${mainTab === 'land' ? 'bg-gradient-to-r from-amber-500 to-orange-500 text-black shadow-lg' : 'text-slate-400 hover:text-white'}`}>
                <Home className="w-3.5 h-3.5" /> 🏡 7/12 & Property
              </button>
            </div>
            {mainTab === 'schemes' && (
              <>
            <div className="rounded-2xl border border-emerald-400/30 bg-emerald-500/5 p-4 mb-4" data-testid="yojana-profile">
              <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                <div className="text-xs uppercase tracking-widest text-emerald-300 font-black">👤 Your Profile (for AI matching)</div>
                {profileSet && <span className="text-[10px] text-emerald-300 opacity-80">✓ Saved · edit anytime</span>}
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                <input type="number" placeholder="Age" value={profile.age} onChange={(e) => setProfile({ ...profile, age: e.target.value })} data-testid="yj-age" className="px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs" />
                <select value={profile.gender} onChange={(e) => setProfile({ ...profile, gender: e.target.value })} data-testid="yj-gender" className="px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs">
                  <option value="">Gender</option>{GENDER_OPTS.map((o) => <option key={o}>{o}</option>)}
                </select>
                <select value={profile.caste} onChange={(e) => setProfile({ ...profile, caste: e.target.value })} data-testid="yj-caste" className="px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs">
                  <option value="">Caste</option>{CASTE_OPTS.map((o) => <option key={o}>{o}</option>)}
                </select>
                <select value={profile.income_range} onChange={(e) => setProfile({ ...profile, income_range: e.target.value })} data-testid="yj-income" className="px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs">
                  <option value="">Income</option>{INCOME_OPTS.map((o) => <option key={o}>{o}</option>)}
                </select>
                <select value={profile.state} onChange={(e) => setProfile({ ...profile, state: e.target.value })} data-testid="yj-state" className="px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs">
                  <option value="">State</option>{(config?.states || []).map((o) => <option key={o}>{o}</option>)}
                </select>
                <select value={profile.profession} onChange={(e) => setProfile({ ...profile, profession: e.target.value })} data-testid="yj-profession" className="px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs">
                  <option value="">Profession</option>{PROF_OPTS.map((o) => <option key={o}>{o}</option>)}
                </select>
              </div>
              <button onClick={saveProfile} disabled={savingProfile} data-testid="yj-save-profile" className="mt-2 w-full px-3 py-2 rounded-lg bg-gradient-to-r from-emerald-500 to-teal-500 text-black text-xs font-black disabled:opacity-40">
                {savingProfile ? 'Saving…' : (profileSet ? '🔄 Update & Refresh Schemes' : '✨ Find My Schemes')}
              </button>
            </div>

            {profileSet && (
              <>
                {/* Category filter */}
                <div className="rounded-2xl border border-slate-700 bg-slate-900/60 p-3 mb-3" data-testid="yojana-categories">
                  <div className="flex items-center gap-1 mb-2 text-[10px] uppercase tracking-widest opacity-70 font-black"><Filter className="w-3 h-3" /> Category</div>
                  <div className="flex flex-wrap gap-1">
                    {['All', ...(config?.categories || [])].map((c) => (
                      <button key={c} onClick={() => setCatFilter(c)} data-testid={`yj-cat-${c.replace(/\s|&/g, '-')}`} className={`px-2 py-1 rounded-lg text-[10px] font-black transition-all ${catFilter === c ? 'bg-emerald-500 text-black' : 'bg-white/5 text-slate-300 hover:bg-white/15'}`}>{c}</button>
                    ))}
                  </div>
                </div>

                {/* Actions */}
                <div className="flex gap-2 flex-wrap mb-3">
                  <button onClick={loadSchemes} data-testid="yj-refresh" className="px-2 py-1 rounded-lg bg-white/10 text-xs font-black flex items-center gap-1"><RefreshCw className="w-3 h-3" /> Refresh</button>
                  <button onClick={downloadPDF} disabled={schemes.length === 0} data-testid="yj-pdf" className="px-2 py-1 rounded-lg bg-gradient-to-r from-cyan-500 to-blue-500 text-black text-xs font-black flex items-center gap-1 disabled:opacity-40"><Download className="w-3 h-3" /> Download PDF ({schemes.length})</button>
                  <button onClick={copyReferral} data-testid="yj-copy-ref" className="px-2 py-1 rounded-lg bg-white/10 text-xs font-black flex items-center gap-1"><Copy className="w-3 h-3" /> Copy Referral Link</button>
                  <button onClick={shareWhatsApp} data-testid="yj-share-wa" className="px-2 py-1 rounded-lg bg-green-500 text-black text-xs font-black flex items-center gap-1"><Users className="w-3 h-3" /> Share on WhatsApp</button>
                </div>

                {/* Schemes list */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="yojana-schemes">
                  {schemes.length === 0 ? (
                    <div className="col-span-2 rounded-2xl border border-slate-700 bg-slate-900/60 p-6 text-center text-sm text-slate-400">
                      <Search className="w-8 h-8 mx-auto mb-2 opacity-40" />
                      No matching schemes for your profile yet. Admin ne schemes publish nahi kiye ya aapke profile ke hisaab se koi match nahi. Try All category ya profile update karo.
                    </div>
                  ) : schemes.map((s) => (
                    <div key={s.id} className="rounded-2xl border border-emerald-400/30 bg-slate-900/60 p-3 hover:border-emerald-400 transition-all" data-testid={`yj-scheme-${s.id}`}>
                      <div className="text-[9px] uppercase tracking-widest text-emerald-300 font-black">{s.category}</div>
                      <div className="text-sm font-black mt-0.5 leading-tight">{s.title}</div>
                      <div className="text-[11px] opacity-80 mt-1 line-clamp-3">{s.description}</div>
                      <div className="flex gap-1.5 mt-2 flex-wrap">
                        <button onClick={() => setDetailScheme(s)} className="text-[10px] px-2 py-0.5 rounded bg-white/10 hover:bg-white/20">Details</button>
                        {s.apply_link && <a href={s.apply_link} target="_blank" rel="noreferrer" className="text-[10px] px-2 py-0.5 rounded bg-emerald-500 text-black font-black flex items-center gap-1"><ExternalLink className="w-2.5 h-2.5" /> Apply</a>}
                        {s.tutorial_link && <a href={s.tutorial_link} target="_blank" rel="noreferrer" className="text-[10px] px-2 py-0.5 rounded bg-red-500 text-white font-black flex items-center gap-1"><Youtube className="w-2.5 h-2.5" /> Guide</a>}
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}

            {/* Affiliate Network info strip */}
            <div className="rounded-2xl border border-amber-400/30 bg-amber-500/5 p-3 mt-4" data-testid="yojana-mlm">
              <div className="text-[10px] uppercase tracking-widest text-amber-300 font-black mb-1 flex items-center gap-1"><Wallet className="w-3 h-3" /> Anant Affiliate — 7-Level Affiliate Network</div>
              <div className="text-xs opacity-90">Every referral subscription pays 33% split across 7 levels. Rates: 10% · 7% · 5% · 4% · 3% · 2% · 2%. Share your link → earn passive income.</div>
            </div>
              </>
            )}

            {/* ===== CSC DIGITAL SERVICES (Netflix-style catalog) ===== */}
            {mainTab === 'csc' && (
              <div data-testid="yojana-csc-catalog">
                <div className="rounded-2xl border-2 border-cyan-400/40 bg-gradient-to-br from-cyan-950/40 via-slate-900 to-blue-950/40 p-4 mb-4">
                  <div className="flex items-center gap-2 mb-1">
                    <Globe className="w-5 h-5 text-cyan-300" />
                    <div className="text-lg font-black bg-gradient-to-r from-cyan-300 to-blue-300 bg-clip-text text-transparent">Digital Services (CSC Hub)</div>
                  </div>
                  <div className="text-xs opacity-80">Direct-links to official government digital services + video tutorials. Zero waiting, zero third-party fees.</div>
                </div>
                {Object.keys(cscGrouped).length === 0 ? (
                  <div className="rounded-2xl border border-slate-700 bg-slate-900/60 p-6 text-center text-sm text-slate-400">
                    <Globe className="w-10 h-10 mx-auto mb-2 opacity-40" />
                    No digital services yet. Admin add karega toh yahaan dikhega.
                  </div>
                ) : (
                  Object.entries(cscGrouped).map(([cat, services]) => (
                    <div key={cat} className="mb-5" data-testid={`csc-cat-${cat.replace(/\s|&/g, '-')}`}>
                      <div className="text-xs uppercase tracking-widest text-cyan-300 font-black mb-2 flex items-center gap-1"><span>▸</span> {cat} <span className="opacity-60 text-[10px]">({services.length})</span></div>
                      <div className="flex gap-3 overflow-x-auto pb-2 -mx-1 px-1" style={{ scrollSnapType: 'x mandatory' }}>
                        {services.map((s) => (
                          <button
                            key={s.id}
                            onClick={() => setDetailCSC(s)}
                            data-testid={`csc-card-${s.id}`}
                            className="flex-shrink-0 w-40 sm:w-48 rounded-xl overflow-hidden border-2 border-cyan-400/30 bg-gradient-to-br from-slate-800 to-slate-900 hover:border-cyan-300 hover:scale-105 transition-all text-left"
                            style={{ scrollSnapAlign: 'start' }}
                          >
                            <div className="relative h-24 sm:h-28 bg-gradient-to-br from-cyan-500/25 to-blue-500/25 flex items-center justify-center">
                              {s.image_icon_url ? (
                                <img src={s.image_icon_url} alt={s.service_name} className="w-14 h-14 sm:w-16 sm:h-16 object-contain" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                              ) : (
                                <Globe className="w-12 h-12 text-cyan-300 opacity-70" />
                              )}
                              <div className="absolute top-1 right-1 w-6 h-6 rounded-full bg-cyan-400/90 text-black flex items-center justify-center">
                                <PlayCircle className="w-3.5 h-3.5" />
                              </div>
                            </div>
                            <div className="p-2">
                              <div className="text-[11px] sm:text-xs font-black leading-tight line-clamp-2">{s.service_name}</div>
                              <div className="text-[9px] opacity-70 mt-0.5">Click to open</div>
                            </div>
                          </button>
                        ))}
                      </div>
                    </div>
                  ))
                )}
                <div className="text-[10px] opacity-60 text-center mt-2">🔒 All links open official government portals in new tab. Anant does not process transactions.</div>
              </div>
            )}
            {mainTab === 'land' && <LandRecordsPanel />}
          </>
        )}

        {/* Trust footer */}
        <div className="mt-6 rounded-2xl border border-slate-800 bg-slate-900/50 p-3 flex items-center gap-2 text-[10px] text-slate-400 justify-center">
          <ShieldCheck className="w-3 h-3" /> Anant Yojana Mitra · 100% client-side scheme filtering · Zero server cost · Powered by Anant Web Portal
        </div>
      </div>

      {/* CSC service detail modal — official link + tutorial */}
      {detailCSC && (
        <div className="fixed inset-0 bg-black/85 z-[110] flex items-center justify-center p-3" data-testid="csc-detail-modal" onClick={() => setDetailCSC(null)}>
          <div className="w-full max-w-2xl max-h-[92vh] overflow-y-auto rounded-2xl border-2 border-cyan-400/60 bg-gradient-to-b from-cyan-950 via-slate-950 to-blue-950 p-5 relative" onClick={(e) => e.stopPropagation()}>
            <button onClick={() => setDetailCSC(null)} className="absolute top-2 right-3 text-lg opacity-70">✕</button>
            <div className="flex items-center gap-3 mb-3">
              {detailCSC.image_icon_url && <img src={detailCSC.image_icon_url} alt="" className="w-14 h-14 rounded-lg bg-white/5 p-1" onError={(e) => { e.currentTarget.style.display = 'none'; }} />}
              <div className="min-w-0">
                <div className="text-[9px] uppercase tracking-widest text-cyan-300 font-black">{detailCSC.category}</div>
                <div className="text-lg font-black leading-tight bg-gradient-to-r from-cyan-300 to-blue-300 bg-clip-text text-transparent">{detailCSC.service_name}</div>
              </div>
            </div>
            {detailCSC.description && <div className="text-xs opacity-90 mb-3">{detailCSC.description}</div>}
            <a href={detailCSC.official_link} target="_blank" rel="noreferrer" data-testid="csc-official-btn" className="block w-full px-4 py-3 rounded-xl bg-gradient-to-r from-cyan-400 to-blue-500 text-black text-sm font-black text-center flex items-center justify-center gap-2 mb-3 hover:scale-105 transition-transform">
              <ExternalLink className="w-4 h-4" /> 🌐 Go to Official Government Website
            </a>
            {detailCSC.training_video_url && (
              <div>
                <div className="text-[10px] uppercase tracking-widest text-red-300 font-black mb-1 flex items-center gap-1"><Youtube className="w-3 h-3" /> How to Fill · Training Guide</div>
                {detailCSC.training_video_url.includes('embed/') && !detailCSC.training_video_url.includes('search_query') ? (
                  <div className="aspect-video rounded-lg overflow-hidden bg-black">
                    <iframe title="Training video" src={detailCSC.training_video_url} allowFullScreen className="w-full h-full" data-testid="csc-embed"></iframe>
                  </div>
                ) : (
                  <a href={detailCSC.training_video_url} target="_blank" rel="noreferrer" className="block w-full px-4 py-2 rounded-lg bg-red-500 text-white text-xs font-black text-center flex items-center justify-center gap-1"><Youtube className="w-3.5 h-3.5" /> Watch on YouTube</a>
                )}
              </div>
            )}
            <div className="text-[9px] opacity-60 mt-3 text-center">🔒 Anant Web Portal directs you to the official site. We do not store your data or process transactions.</div>
          </div>
        </div>
      )}

      {/* Scheme detail modal */}
      {detailScheme && (
        <div className="fixed inset-0 bg-black/80 z-[110] flex items-center justify-center p-3" data-testid="yj-detail-modal" onClick={() => setDetailScheme(null)}>
          <div className="w-full max-w-xl max-h-[90vh] overflow-y-auto rounded-2xl border-2 border-emerald-400/50 bg-gradient-to-b from-slate-900 to-slate-950 p-5 relative" onClick={(e) => e.stopPropagation()}>
            <button onClick={() => setDetailScheme(null)} className="absolute top-2 right-3 text-lg opacity-70">✕</button>
            <div className="text-[9px] uppercase tracking-widest text-emerald-300 font-black">{detailScheme.category}</div>
            <div className="text-lg font-black mt-1">{detailScheme.title}</div>
            <div className="text-xs opacity-80 mt-2 whitespace-pre-wrap">{detailScheme.description}</div>
            {detailScheme.benefits && (<><div className="text-[9px] uppercase tracking-widest text-amber-300 font-black mt-3">Benefits</div>
              <div className="text-xs opacity-90 whitespace-pre-wrap">{detailScheme.benefits}</div></>)}
            <div className="flex gap-2 mt-4 flex-wrap">
              {detailScheme.apply_link && <a href={detailScheme.apply_link} target="_blank" rel="noreferrer" className="px-3 py-2 rounded-lg bg-emerald-500 text-black text-xs font-black flex items-center gap-1"><ExternalLink className="w-3 h-3" /> Apply Now</a>}
              {detailScheme.tutorial_link && <a href={detailScheme.tutorial_link} target="_blank" rel="noreferrer" className="px-3 py-2 rounded-lg bg-red-500 text-white text-xs font-black flex items-center gap-1"><Youtube className="w-3 h-3" /> How to Fill</a>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


// ============ ANANT 7/12 & PROPERTY PORTAL — Land Records Panel ============
const LR_STATUS = {
  PROCESSING: { c: 'bg-amber-500/25 text-amber-300', t: 'Processing' },
  COMPLETED:  { c: 'bg-emerald-500/25 text-emerald-300', t: 'Completed' },
  REFUNDED:   { c: 'bg-red-500/25 text-red-300', t: 'Refunded' },
  FAILED:     { c: 'bg-red-500/25 text-red-300', t: 'Failed' },
  PENDING:    { c: 'bg-slate-500/25 text-slate-300', t: 'Pending' },
};
function LandStatusBadge({ s }) {
  const cfg = LR_STATUS[s] || LR_STATUS.PENDING;
  const Icon = s === 'COMPLETED' ? CheckCircle2 : (s === 'REFUNDED' || s === 'FAILED' ? XCircle : Clock);
  return <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-black uppercase ${cfg.c}`}><Icon className={`w-3 h-3 ${s === 'PROCESSING' ? 'animate-pulse' : ''}`} /> {cfg.t}</span>;
}

function LandRecordsPanel() {
  const [lrConfig, setLrConfig] = useState(null);
  const [orders, setOrders] = useState([]);
  const [form, setForm] = useState({
    service_type: '7_12_8A', state: 'MH', district: '', taluka: '', village: '',
    search_mode: 'SURVEY_NO', survey_number: '', owner_name: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [polling, setPolling] = useState({});   // {order_id: intervalId}

  const loadAll = useCallback(async () => {
    try {
      const [c, o] = await Promise.all([
        api.get('/anant-yojana/land-records/config'),
        api.get('/anant-yojana/land-records/my-orders'),
      ]);
      setLrConfig(c.data);
      setOrders(o.data?.orders || []);
    } catch { /* silent */ }
  }, []);
  useEffect(() => { loadAll(); }, [loadAll]);

  // Poll any PROCESSING order every 3s
  useEffect(() => {
    const processing = orders.filter((o) => o.status === 'PROCESSING');
    processing.forEach((o) => {
      if (polling[o.order_id]) return;
      const iv = setInterval(async () => {
        try {
          const r = await api.get(`/anant-yojana/land-records/orders/${o.order_id}`);
          setOrders((prev) => prev.map((p) => p.order_id === o.order_id ? r.data.order : p));
          if (r.data.order.status !== 'PROCESSING') {
            clearInterval(iv);
            setPolling((prev) => { const c = { ...prev }; delete c[o.order_id]; return c; });
            if (r.data.order.status === 'COMPLETED') toast.success(`✅ Report ready: ${o.order_id}`);
            if (r.data.order.status === 'REFUNDED') toast.error(`💸 Failed — 100 AT auto-refunded: ${o.order_id}`);
          }
        } catch { clearInterval(iv); }
      }, 3000);
      setPolling((prev) => ({ ...prev, [o.order_id]: iv }));
    });
    return () => { Object.values(polling).forEach(clearInterval); };
  }, [orders]);   // eslint deps: only depend on orders

  const submit = async () => {
    if (!form.district.trim() || !form.taluka.trim() || !form.village.trim()) { toast.error('District, Taluka, Village zaroori hai'); return; }
    if (form.search_mode === 'SURVEY_NO' && !form.survey_number.trim()) { toast.error('Survey/Gat number bharo'); return; }
    if (form.search_mode === 'OWNER_NAME' && !form.owner_name.trim()) { toast.error('Malik ka naam bharo'); return; }
    if (!window.confirm(`100 AT debit hoga aur order queue ho jayega — confirm?`)) return;
    setSubmitting(true);
    try {
      const r = await api.post('/anant-yojana/land-records/create', form);
      toast.success(`Order queued: ${r.data.order.order_id} — ETA 10-30s`);
      setForm({ ...form, survey_number: '', owner_name: '' });
      await loadAll();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Order create failed'); }
    finally { setSubmitting(false); }
  };

  const openPdf = (order) => {
    // Open the HTML report in a new tab (browser can then print/save as PDF)
    const token = localStorage.getItem('token');
    const url = `${process.env.REACT_APP_BACKEND_URL}${order.pdf_url}`;
    // Since HTML report needs auth, fetch it and open in blob URL
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.text())
      .then((html) => {
        const blob = new Blob([html], { type: 'text/html' });
        const u = URL.createObjectURL(blob);
        window.open(u, '_blank');
        setTimeout(() => URL.revokeObjectURL(u), 60000);
      })
      .catch(() => toast.error('PDF open failed'));
  };

  if (!lrConfig) return <div className="text-center py-8 text-sm opacity-70"><Loader2 className="w-5 h-5 animate-spin inline mr-2" />Loading…</div>;

  return (
    <div data-testid="yojana-land-panel">
      {/* Hero */}
      <div className="relative overflow-hidden rounded-3xl border-2 border-amber-400/50 bg-gradient-to-br from-amber-950 via-orange-900/40 to-red-950 p-5 mb-4">
        <div className="absolute -top-16 -right-16 w-56 h-56 rounded-full bg-amber-500/25 blur-3xl pointer-events-none animate-pulse"></div>
        <div className="relative">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-amber-500/25 border border-amber-400/40 text-[10px] font-black tracking-widest text-amber-200 mb-2">
            <Home className="w-3 h-3" /> ANANT 7/12 & PROPERTY PORTAL · 100 AT / Report
          </div>
          <div className="text-2xl sm:text-3xl font-black bg-gradient-to-r from-amber-300 via-orange-200 to-yellow-300 bg-clip-text text-transparent">7/12, Property Card, Index-2 · 30 सेकंड में</div>
          <div className="text-xs opacity-90 mt-1">100 AT wallet se auto-debit → 3 retry → fail hone par <b>100% auto refund</b> — bina admin ke turant delivery.</div>
          <div className="flex flex-wrap gap-1 mt-3">
            {lrConfig.features.map((f, i) => <span key={i} className="text-[10px] px-2 py-0.5 rounded bg-amber-500/15 border border-amber-400/30 text-amber-100 font-black">✓ {f}</span>)}
          </div>
        </div>
      </div>

      {/* Form */}
      <div className="rounded-2xl border-2 border-amber-400/40 bg-slate-900/70 p-4 mb-4" data-testid="land-form">
        <div className="text-xs uppercase tracking-widest text-amber-300 font-black mb-3 flex items-center gap-1"><FileText className="w-3 h-3" /> New Land Record Request · 100 AT</div>

        {/* Service Type */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-1 mb-3">
          {lrConfig.service_types.map((st) => (
            <button key={st.key} onClick={() => setForm({ ...form, service_type: st.key })} data-testid={`land-svc-${st.key}`}
              className={`px-2 py-2 rounded-lg border-2 text-[10px] font-black transition-all ${form.service_type === st.key ? 'border-amber-300 bg-amber-500/20' : 'border-slate-700 bg-slate-800/50 hover:border-amber-400/50'}`}>
              {st.label}<div className="text-[9px] opacity-70 font-normal mt-0.5">ETA: {st.delivery_sec}</div>
            </button>
          ))}
        </div>

        {/* State + Location */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-2">
          <select value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} data-testid="land-state" className="px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs">
            {lrConfig.states.map((s) => <option key={s.code} value={s.code} disabled={!s.active}>{s.label} {!s.active && '(Coming Soon)'}</option>)}
          </select>
          <input placeholder="जिला / District" value={form.district} onChange={(e) => setForm({ ...form, district: e.target.value })} data-testid="land-district" className="px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs" />
          <input placeholder="तालुका / Taluka" value={form.taluka} onChange={(e) => setForm({ ...form, taluka: e.target.value })} data-testid="land-taluka" className="px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs" />
          <input placeholder="गांव / Village" value={form.village} onChange={(e) => setForm({ ...form, village: e.target.value })} data-testid="land-village" className="px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs" />
        </div>

        {/* Search Mode Toggle */}
        <div className="flex gap-1 p-1 rounded-lg bg-slate-800 mb-2">
          <button onClick={() => setForm({ ...form, search_mode: 'SURVEY_NO' })} data-testid="land-mode-survey"
            className={`flex-1 px-2 py-1.5 rounded text-[11px] font-black transition-all ${form.search_mode === 'SURVEY_NO' ? 'bg-amber-500 text-black' : 'text-slate-400'}`}>🔢 Survey / Gat No.</button>
          <button onClick={() => setForm({ ...form, search_mode: 'OWNER_NAME' })} data-testid="land-mode-owner"
            className={`flex-1 px-2 py-1.5 rounded text-[11px] font-black transition-all ${form.search_mode === 'OWNER_NAME' ? 'bg-amber-500 text-black' : 'text-slate-400'}`}>👤 नावाने शोधा (Owner Name)</button>
        </div>
        {form.search_mode === 'SURVEY_NO' ? (
          <input placeholder="e.g. 45/2A" value={form.survey_number} onChange={(e) => setForm({ ...form, survey_number: e.target.value })} data-testid="land-survey" className="w-full px-2 py-2 rounded bg-slate-800 border border-slate-700 text-sm font-mono" />
        ) : (
          <input placeholder="Owner first/last name (Marathi/English)" value={form.owner_name} onChange={(e) => setForm({ ...form, owner_name: e.target.value })} data-testid="land-owner" className="w-full px-2 py-2 rounded bg-slate-800 border border-slate-700 text-sm" />
        )}

        <button onClick={submit} disabled={submitting} data-testid="land-submit"
          className="w-full mt-3 px-4 py-3 rounded-xl bg-gradient-to-r from-amber-400 via-orange-400 to-red-500 text-black text-sm font-black disabled:opacity-40">
          {submitting ? '⏳ Queuing…' : '💰 100 AT पे करें और रिपोर्ट पाएं'}
        </button>
        <div className="text-[10px] opacity-60 text-center mt-1">✓ Auto-refund guaranteed on 3-retry failure · Portal: {(lrConfig.states.find((s) => s.code === form.state) || {}).portal}</div>
      </div>

      {/* Orders List */}
      <div className="rounded-2xl border border-slate-700 bg-slate-900/60 p-4" data-testid="land-orders">
        <div className="flex items-center justify-between mb-2">
          <div className="text-xs uppercase tracking-widest text-amber-300 font-black flex items-center gap-1"><FileText className="w-3 h-3" /> My Reports ({orders.length})</div>
          <button onClick={loadAll} className="text-[10px] px-2 py-1 rounded bg-white/10 flex items-center gap-1"><RotateCw className="w-3 h-3" /> Refresh</button>
        </div>
        {orders.length === 0 ? (
          <div className="text-center py-6 text-xs opacity-60">No reports yet. Submit your first request above ↑</div>
        ) : (
          <div className="space-y-2">
            {orders.map((o) => (
              <div key={o.order_id} className="rounded-xl border border-slate-700 bg-slate-800/50 p-3" data-testid={`land-order-${o.order_id}`}>
                <div className="flex items-center justify-between mb-1 flex-wrap gap-1">
                  <div>
                    <div className="text-xs font-black">{(lrConfig.service_types.find((s) => s.key === o.service_type) || {}).label || o.service_type}</div>
                    <div className="text-[10px] opacity-70 font-mono">{o.order_id} · {new Date(o.created_at).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}</div>
                  </div>
                  <LandStatusBadge s={o.status} />
                </div>
                <div className="text-[10px] opacity-80">📍 {o.village}, {o.taluka}, {o.district} · {o.search_mode === 'SURVEY_NO' ? `Survey ${o.survey_number}` : `Owner: ${o.owner_name}`}</div>
                {o.status === 'PROCESSING' && (
                  <div className="mt-2">
                    <div className="h-1 rounded-full bg-slate-700 overflow-hidden"><div className="h-full bg-gradient-to-r from-amber-400 to-orange-500 animate-pulse" style={{ width: '60%' }}></div></div>
                    <div className="text-[10px] text-amber-300 mt-1">Fetching from portal… (retries: {o.retry_count || 0}/3)</div>
                  </div>
                )}
                {o.status === 'COMPLETED' && (
                  <>
                    {o.ai_summary && (
                      <div className="mt-2 rounded-lg bg-emerald-500/10 border border-emerald-400/30 p-2">
                        <div className="text-[9px] uppercase text-emerald-300 font-black mb-0.5">📝 AI सरल हिंदी Summary</div>
                        <div className="text-[11px] leading-snug">{o.ai_summary}</div>
                      </div>
                    )}
                    <div className="flex gap-2 mt-2 flex-wrap">
                      <button onClick={() => openPdf(o)} data-testid={`land-pdf-${o.order_id}`} className="px-3 py-1.5 rounded-lg bg-gradient-to-r from-amber-400 to-orange-500 text-black text-[11px] font-black flex items-center gap-1"><Download className="w-3 h-3" /> Download PDF</button>
                      {o.wa_share_url && <a href={o.wa_share_url} target="_blank" rel="noreferrer" data-testid={`land-wa-${o.order_id}`} className="px-3 py-1.5 rounded-lg bg-green-500 text-black text-[11px] font-black flex items-center gap-1"><MessageCircle className="w-3 h-3" /> Share on WhatsApp</a>}
                    </div>
                  </>
                )}
                {o.status === 'REFUNDED' && (
                  <div className="mt-2 text-[10px] text-red-300">💸 3 retries fail — <b>100 AT auto-refunded</b> to your wallet. {o.failure_reason || ''}</div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="text-[10px] opacity-60 text-center mt-2">🔒 Reports are AI-assisted extracts. For legal use, always verify against original government record.</div>
    </div>
  );
}
